import { useState, useRef, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Crop, RotateCw, ZoomIn, Check, X, Scissors } from "lucide-react";

// Video'dan frame çıkartma ve crop uygulama - basitleştirilmiş versiyon
const createCroppedVideo = async (videoSrc, croppedAreaPixels, aspectRatio) => {
  // Tarayıcıda karmaşık video işleme sorunlu olduğundan
  // sadece orijinal videoyu döndür
  return videoSrc;
};

const VideoCropper = ({ open, onClose, videoSrc, onCropComplete }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(9/16); // Default Instagram Reels aspect
  const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });
  const videoRef = useRef(null);
  
  const aspectRatios = [
    { label: "9:16 (Reels/Story)", value: 9/16 },
    { label: "1:1 (Kare)", value: 1 },
    { label: "4:5 (Instagram)", value: 4/5 },
    { label: "16:9 (Yatay)", value: 16/9 },
    { label: "4:3 (Klasik)", value: 4/3 },
  ];

  useEffect(() => {
    if (videoSrc && videoRef.current) {
      const video = videoRef.current;
      video.onloadedmetadata = () => {
        setVideoDimensions({
          width: video.videoWidth,
          height: video.videoHeight
        });
      };
    }
  }, [videoSrc]);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropAreaChange = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleApplyCrop = async () => {
    if (!croppedAreaPixels) return;
    
    setProcessing(true);
    try {
      const croppedVideo = await createCroppedVideo(videoSrc, croppedAreaPixels, aspectRatio);
      onCropComplete(croppedVideo);
      onClose();
    } catch (error) {
      console.error("Video işleme hatası:", error);
      // Fallback: Orijinal videoyu döndür
      onCropComplete(videoSrc);
      onClose();
    } finally {
      setProcessing(false);
    }
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const resetCrop = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5 text-emerald-600" />
            Video Düzenle
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Video Cropper Area */}
          <div className="relative h-[350px] bg-black rounded-lg overflow-hidden">
            {videoSrc && (
              <Cropper
                video={videoSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspectRatio}
                onCropChange={onCropChange}
                onZoomChange={onZoomChange}
                onCropComplete={onCropAreaChange}
                objectFit="contain"
              />
            )}
            {/* Hidden video for metadata */}
            <video 
              ref={videoRef} 
              src={videoSrc} 
              className="hidden" 
              muted 
              playsInline
            />
          </div>
          
          {/* Aspect Ratio Selection */}
          <div className="flex items-center gap-3">
            <Label className="text-sm font-medium whitespace-nowrap">Oran:</Label>
            <Select 
              value={aspectRatio.toString()} 
              onValueChange={(val) => setAspectRatio(parseFloat(val))}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Oran seç" />
              </SelectTrigger>
              <SelectContent>
                {aspectRatios.map((ratio) => (
                  <SelectItem key={ratio.value} value={ratio.value.toString()}>
                    {ratio.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Zoom Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium flex items-center gap-2">
                <ZoomIn className="h-4 w-4" />
                Yakınlaştır
              </Label>
              <span className="text-sm text-gray-500">{Math.round(zoom * 100)}%</span>
            </div>
            <Slider
              value={[zoom]}
              onValueChange={([val]) => setZoom(val)}
              min={1}
              max={3}
              step={0.1}
              className="w-full"
            />
          </div>
          
          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRotate}
              className="flex-1"
            >
              <RotateCw className="h-4 w-4 mr-2" />
              Döndür
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={resetCrop}
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" />
              Sıfırla
            </Button>
          </div>
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={processing}>
            İptal
          </Button>
          <Button 
            onClick={handleApplyCrop} 
            disabled={processing}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                İşleniyor...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Uygula
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VideoCropper;
